import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  lazy,
  Suspense,
} from 'react';
import { useAtomValue } from 'jotai';
import { establishmentsArrayAtom } from 'atoms/establishments-atoms';
import { InlineLoading, Button, Tag } from '@carbon/react';
import { FloatingTooltip } from 'components/floating-tooltip';
import {
  OpenPanelLeft,
  OpenPanelRight,
  MapBoundary,
  DataConnected,
  DataTable,
  Time,
  DocumentMultiple_01 as DocumentMultiple01,
  View,
  Filter,
  Close,
  ChevronDown,
} from '@carbon/react/icons';
import classNames from 'classnames';
import {
  OwnershipGraph,
  ConveyanceEdge,
  OwnershipNode,
  TitleChainData,
  DocumentDetailMap,
  PathToReview,
  getDocumentForEdge,
} from './types';

import { useIntersectingProperties } from './hooks/use-intersecting-properties';
import { getEdgeTypeLabel, getNodes, getEdges } from './data-helpers';
import {
  OwnershipTanstackTable,
  OwnershipGroupBy,
} from './ownership-table-columns';
import { CitationTracingPanel } from './citation-tracing-panel';

import { DEFAULT_OWNERSHIP_TABLE_CONFIG } from './ownership-table-settings';
import { OwnershipMap, MapGeography } from './ownership-map';
import {
  ConveyanceRunsheet,
  groupEdgesByDocument,
} from './conveyance-runsheet';
import { DocumentReviewPanel } from './document-review-panel';
import {
  TitleChainValidatorFilters,
  LayoutSettings,
  PanelVisibilityControls,
  PanelLayoutControls,
  DocumentIndexFilterControls,
} from './v2-sidebar';
import styles from './v2-results-view.module.scss';

// Lazy-load GoJS graph (it's heavy)
const TitleValidatorGraph = lazy(() => import('./graph/v2-graph-viewer'));

// =============================================================================
// Helper: build adjacency maps from a filtered set of edges
// =============================================================================

export function buildAdjacency(edges: Record<string, ConveyanceEdge>): {
  adjacency: Record<string, string[]>;
  reverseAdjacency: Record<string, string[]>;
} {
  const adjacency: Record<string, string[]> = {};
  const reverseAdjacency: Record<string, string[]> = {};
  for (const edge of Object.values(edges)) {
    if (edge.fromNodeId === null || edge.toNodeId === null) continue;
    const fromKey = String(edge.fromNodeId);
    const toKey = String(edge.toNodeId);
    const edgeKey = String(edge.id);
    if (!adjacency[fromKey]) adjacency[fromKey] = [];
    adjacency[fromKey].push(edgeKey);
    if (!reverseAdjacency[toKey]) reverseAdjacency[toKey] = [];
    reverseAdjacency[toKey].push(edgeKey);
  }
  return { adjacency, reverseAdjacency };
}

// * Build a filtered OwnershipGraph containing only edges from the given
// document IDs, plus null-doc edges (E5/E6) whose endpoints are reachable.
// @param fullGraph The complete ownership graph.
// @param docIds Set of document IDs to include.
// @returns A filtered OwnershipGraph with only matching edges and reachable nodes.
export function filterGraphByDocumentIds(
  fullGraph: OwnershipGraph,
  docIds: Set<number>
): OwnershipGraph {
  const allEdges = getEdges(fullGraph);

  const docEdges = allEdges.filter(
    (e) => e.documentId !== null && docIds.has(e.documentId)
  );

  const docNodeIds = new Set<string>();
  for (const edge of docEdges) {
    docNodeIds.add(String(edge.fromNodeId));
    docNodeIds.add(String(edge.toNodeId));
  }

  // Include null-doc edges (E5, E6) if both endpoints are reachable
  const nullDocEdges = allEdges.filter(
    (e) =>
      e.documentId === null &&
      docNodeIds.has(String(e.fromNodeId)) &&
      docNodeIds.has(String(e.toNodeId))
  );

  const filteredEdgesList = [...docEdges, ...nullDocEdges];
  const reachableNodeIds = new Set<string>();
  for (const edge of filteredEdgesList) {
    reachableNodeIds.add(String(edge.fromNodeId));
    reachableNodeIds.add(String(edge.toNodeId));
  }

  // Include establishment root nodes
  for (const [id] of Object.entries(fullGraph.nodes)) {
    const inbound = allEdges.filter((e) => String(e.toNodeId) === id);
    if (
      inbound.length === 0 &&
      allEdges.some(
        (e) =>
          String(e.fromNodeId) === id &&
          filteredEdgesList.some((fe) => fe.id === e.id)
      )
    ) {
      reachableNodeIds.add(id);
    }
  }

  const filteredNodes: Record<string, OwnershipNode> = {};
  for (const [id, node] of Object.entries(fullGraph.nodes)) {
    if (reachableNodeIds.has(id)) filteredNodes[id] = node;
  }
  const filteredEdges: Record<string, ConveyanceEdge> = {};
  for (const edge of filteredEdgesList) {
    filteredEdges[edge.id] = edge;
  }
  const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
  return {
    nodes: filteredNodes,
    edges: filteredEdges,
    adjacency,
    reverseAdjacency,
  };
}

// =============================================================================
// Path tracing utility — BFS backward through inbound edges to root ancestors
// =============================================================================

export function tracePathToRoot(
  nodeId: string,
  graph: OwnershipGraph
): { nodeIds: string[]; edgeIds: string[] } {
  const allEdges = Object.values(graph.edges);
  // Coerce all IDs to strings — real data uses numeric IDs
  const inboundMap = new Map<string, ConveyanceEdge[]>();
  for (const edge of allEdges) {
    const toKey = String(edge.toNodeId);
    if (!inboundMap.has(toKey)) {
      inboundMap.set(toKey, []);
    }
    inboundMap.get(toKey)!.push(edge);
  }

  const visitedNodes = new Set<string>();
  const visitedEdges = new Set<string>();
  const queue = [String(nodeId)];
  visitedNodes.add(String(nodeId));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const inbound = inboundMap.get(current) || [];
    for (const edge of inbound) {
      const edgeKey = String(edge.id);
      const fromKey = String(edge.fromNodeId);
      visitedEdges.add(edgeKey);
      if (!visitedNodes.has(fromKey)) {
        visitedNodes.add(fromKey);
        queue.push(fromKey);
      }
    }
  }

  return {
    nodeIds: Array.from(visitedNodes),
    edgeIds: Array.from(visitedEdges),
  };
}

// =============================================================================
// Header Menus (Layout + Filters)
// =============================================================================

function LayoutMenu({
  layoutSettings,
  onLayoutChange,
}: {
  layoutSettings: LayoutSettings;
  onLayoutChange: (settings: Partial<LayoutSettings>) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <>
      <div className={styles.headerMenuTrigger} ref={buttonRef}>
        <FloatingTooltip
          content="Panel Layout Configuration"
          placement="bottom"
        >
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={View}
            iconDescription=""
            onClick={handleOpen}
          />
        </FloatingTooltip>
      </div>
      {isOpen && (
        <div
          className={styles.menuPanel}
          ref={panelRef}
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          <div className={styles.menuPanelHeader}>
            <h4>Panel Layout Configuration</h4>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Close}
              iconDescription="Close"
              onClick={() => setIsOpen(false)}
            />
          </div>
          <div className={styles.menuPanelContent}>
            <div className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Panel Visibility</span>
                <span className={styles.settingDescription}>
                  Toggle visible panels
                </span>
              </div>
              <PanelVisibilityControls
                layoutSettings={layoutSettings}
                onLayoutChange={onLayoutChange}
                tooltipPlacement="bottom"
              />
            </div>

            {layoutSettings.showGraph &&
              (layoutSettings.showCurrentOwnership ||
                layoutSettings.showRunsheet) && (
                <div className={styles.settingRow}>
                  <div className={styles.settingInfo}>
                    <span className={styles.settingLabel}>
                      Panel Orientation
                    </span>
                    <span className={styles.settingDescription}>
                      Arrange panel orientation
                    </span>
                  </div>
                  <PanelLayoutControls
                    layoutSettings={layoutSettings}
                    onLayoutChange={onLayoutChange}
                    tooltipPlacement="bottom"
                  />
                </div>
              )}
          </div>
        </div>
      )}
    </>
  );
}

function FiltersMenu({
  filters,
  uniqueLocations,
  onFiltersChange,
  onResetFilters,
}: {
  filters: TitleChainValidatorFilters;
  uniqueLocations: string[];
  onFiltersChange: (filters: Partial<TitleChainValidatorFilters>) => void;
  onResetFilters: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const hasActiveFilters =
    filters.selectedLocations.length > 0 ||
    filters.interestTypeFilter.length > 0 ||
    filters.edgeTypeFilter.length > 0;

  const handleOpen = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <>
      <div className={styles.headerMenuTrigger} ref={buttonRef}>
        <FloatingTooltip content="Filter Validation Results" placement="bottom">
          <Button
            kind={hasActiveFilters ? 'primary' : 'ghost'}
            size="sm"
            hasIconOnly
            renderIcon={Filter}
            iconDescription=""
            onClick={handleOpen}
          />
        </FloatingTooltip>
      </div>
      {isOpen && (
        <div
          className={`${styles.menuPanel} ${styles.menuPanelFilters}`}
          ref={panelRef}
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          <div className={styles.menuPanelHeader}>
            <h4>Filter Validation Results</h4>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Close}
              iconDescription="Close"
              onClick={() => setIsOpen(false)}
            />
          </div>
          <div className={styles.menuPanelContent}>
            <DocumentIndexFilterControls
              filters={filters}
              uniqueLocations={uniqueLocations}
              onFiltersChange={onFiltersChange}
              onResetFilters={onResetFilters}
            />
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// Main Component
// =============================================================================

type SelectionBanner =
  | {
      type: 'pathSet';
      entityName: string;
      geometry: string;
      effectiveDate: string | null;
      pathSetDocumentCount: number;
      establishmentName: string | null;
      isCurrentOwner: boolean;
      isEstablishment: boolean;
    }
  | {
      type: 'document';
      documentId: number;
      documentTitle: string;
      operationCount: number;
      partyCount: number;
      effectiveDate: string | null;
    }
  | {
      type: 'operation';
      edgeTypeLabel: string;
      fromEntityName: string;
      toEntityName: string;
      effectiveDate: string;
      edgeCount: number;
    };

interface ValidationResultsProps {
  chainData: TitleChainData;
  hasNothingToGraph?: boolean;
  filters: TitleChainValidatorFilters;
  onFiltersChange: (filters: Partial<TitleChainValidatorFilters>) => void;
  onResetFilters: () => void;
  uniqueLocations: string[];
  layoutSettings: LayoutSettings;
  onLayoutChange: (settings: Partial<LayoutSettings>) => void;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  documentTitle?: string;
  documentId?: number;
  documentHref?: string;
  reconcilerUrl?: string;
}

export function ValidationResults({
  chainData,
  hasNothingToGraph,
  filters,
  onFiltersChange,
  onResetFilters,
  uniqueLocations,
  layoutSettings,
  onLayoutChange,
  sidebarCollapsed,
  onToggleSidebar,
  documentTitle,
  documentId,
  documentHref,
  reconcilerUrl,
}: ValidationResultsProps) {
  // Ownership table column configuration
  const ownershipTableConfig = DEFAULT_OWNERSHIP_TABLE_CONFIG;
  const [ownershipGroupBy, setOwnershipGroupBy] =
    useState<OwnershipGroupBy>('none');

  // Graph-to-table selection sync — node, edge, and document IDs
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [expandConvergent, setExpandConvergent] = useState(true);

  // Ref for imperatively clearing the toolbar inside TitleValidatorGraph
  const clearToolbarRef = useRef<(() => void) | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    number | null
  >(null);
  const [docReviewMode, setDocReviewMode] = useState(false);
  const [reviewDocIndex, setReviewDocIndex] = useState<number | null>(null);
  const [ownershipDiffMode, setOwnershipDiffMode] = useState<'all' | 'changes'>(
    'all'
  );

  // Asset preview — disabled for now

  // Draggable panel splits — null = default 50/50 flex
  const [horizontalSplit, setHorizontalSplit] = useState<number | null>(null);
  const [verticalSplit, setVerticalSplit] = useState<number | null>(null);
  const [ownershipMapSplit, setOwnershipMapSplit] = useState<number | null>(
    null
  );
  const [showLineage, setShowLineage] = useState(false);
  const [lineageSplit, setLineageSplit] = useState<number | null>(null);
  const tableLineageRef = useRef<HTMLDivElement>(null);
  const viewPanelsRef = useRef<HTMLDivElement>(null);
  const tablePanelRef = useRef<HTMLDivElement>(null);
  const ownershipPanelRef = useRef<HTMLDivElement>(null);

  // Get the ownership graph and documents from chain data
  const ownershipGraph: OwnershipGraph = chainData.graph;
  const documents = useMemo(() => chainData?.documents ?? {}, [chainData]);

  // Header bar metadata
  const establishments = useAtomValue(establishmentsArrayAtom);
  const uniqueEstablishmentNames = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const e of establishments) {
      if (!seen.has(e.referenceLocationName)) {
        seen.add(e.referenceLocationName);
        names.push(e.referenceLocationName);
      }
    }
    return names;
  }, [establishments]);

  const targetEntityName = useMemo(() => {
    const subjectIds = chainData.subjectAssetNodeIds ?? [];
    for (const id of subjectIds) {
      const node = chainData.graph.nodes[String(id)];
      if (node) return node.entity.entityName;
    }
    return null;
  }, [chainData]);

  // Timeline scroller state — null means "show all"
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedStartDate] = useState<string | null>(null);

  // Date range from edge dates (actual dates, not just years)
  const timelineRange = useMemo(() => {
    const allEdges = getEdges(ownershipGraph);
    if (allEdges.length === 0) return null;
    const dates = allEdges
      .map((e) => getDocumentForEdge(e, documents)?.effectiveDate ?? null)
      .filter((d): d is string => d !== null)
      .sort();
    if (dates.length === 0) return null;
    return { minDate: dates[0], maxDate: dates[dates.length - 1] };
  }, [ownershipGraph, documents]);

  // Documents grouped by document_id — must be computed before timelineFilteredGraph
  // because the document-order filter depends on it
  const allSortedDocuments = useMemo(() => {
    const docs = groupEdgesByDocument(
      ownershipGraph,
      documents,
      {} as DocumentDetailMap
    );
    docs.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    return docs;
  }, [ownershipGraph, documents]);

  // Document-order filter: when in review mode, include only edges from
  // documents at positions reviewDocIndex..end (oldest first, array is newest-first)
  const includedDocumentIds = useMemo(() => {
    if (!docReviewMode || reviewDocIndex === null) return null;
    const ids = new Set<number>();
    for (let i = allSortedDocuments.length - 1; i >= reviewDocIndex; i--) {
      ids.add(allSortedDocuments[i].documentId);
    }
    return ids;
  }, [docReviewMode, reviewDocIndex, allSortedDocuments]);

  // "Before" document IDs — all docs BEFORE the current review doc (for diff)
  const beforeDocumentIds = useMemo(() => {
    if (!docReviewMode || reviewDocIndex === null) return null;
    const ids = new Set<number>();
    for (let i = allSortedDocuments.length - 1; i > reviewDocIndex; i--) {
      ids.add(allSortedDocuments[i].documentId);
    }
    return ids;
  }, [docReviewMode, reviewDocIndex, allSortedDocuments]);

  // Ownership graph BEFORE the current document — for diff mode
  const beforeDocGraph = useMemo((): OwnershipGraph | null => {
    if (!docReviewMode || reviewDocIndex === null) return null;
    if (beforeDocumentIds && beforeDocumentIds.size > 0) {
      return filterGraphByDocumentIds(ownershipGraph, beforeDocumentIds);
    }
    // First document — "before" is just the establishment root nodes
    // (nodes with no inbound edges). Build a minimal graph with only those.
    const allEdges = Object.values(ownershipGraph.edges);
    const nodesWithInbound = new Set<string>();
    for (const edge of allEdges) {
      if (edge.toNodeId !== null) nodesWithInbound.add(String(edge.toNodeId));
    }
    const rootNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(ownershipGraph.nodes)) {
      if (!nodesWithInbound.has(id)) rootNodes[id] = node;
    }
    if (Object.keys(rootNodes).length === 0) return null;
    return {
      nodes: rootNodes,
      edges: {},
      adjacency: {},
      reverseAdjacency: {},
    };
  }, [ownershipGraph, beforeDocumentIds, docReviewMode, reviewDocIndex]);

  // Filter ownership graph by timeline (both start and end dates)
  const timelineFilteredGraph = useMemo((): OwnershipGraph => {
    const allEdges = getEdges(ownershipGraph);

    // ---------------------------------------------------------------
    // Document-order filter: in review mode, include only edges from
    // documents up to the current position (instead of date filtering)
    // ---------------------------------------------------------------
    if (includedDocumentIds) {
      const docEdges = allEdges.filter(
        (e) => e.documentId !== null && includedDocumentIds.has(e.documentId)
      );

      const docNodeIds = new Set<string>();
      for (const edge of docEdges) {
        docNodeIds.add(String(edge.fromNodeId));
        docNodeIds.add(String(edge.toNodeId));
      }

      // Include null-doc edges (E5, E6) if both endpoints are reachable
      const nullDocEdges = allEdges.filter(
        (e) =>
          e.documentId === null &&
          docNodeIds.has(String(e.fromNodeId)) &&
          docNodeIds.has(String(e.toNodeId))
      );

      const filteredEdgesList = [...docEdges, ...nullDocEdges];
      const reachableNodeIds = new Set<string>();
      for (const edge of filteredEdgesList) {
        reachableNodeIds.add(String(edge.fromNodeId));
        reachableNodeIds.add(String(edge.toNodeId));
      }

      // Include establishment root nodes with outbound edges in the set
      for (const [id] of Object.entries(ownershipGraph.nodes)) {
        const inbound = allEdges.filter((e) => String(e.toNodeId) === id);
        if (
          inbound.length === 0 &&
          allEdges.some(
            (e) =>
              String(e.fromNodeId) === id &&
              filteredEdgesList.some((fe) => fe.id === e.id)
          )
        ) {
          reachableNodeIds.add(id);
        }
      }

      const filteredNodes: Record<string, OwnershipNode> = {};
      for (const [id, node] of Object.entries(ownershipGraph.nodes)) {
        if (reachableNodeIds.has(id)) filteredNodes[id] = node;
      }
      const filteredEdges: Record<string, ConveyanceEdge> = {};
      for (const edge of filteredEdgesList) {
        filteredEdges[edge.id] = edge;
      }
      const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
      return {
        nodes: filteredNodes,
        edges: filteredEdges,
        adjacency,
        reverseAdjacency,
      };
    }

    // ---------------------------------------------------------------
    // Normal date-based filtering (unchanged)
    // ---------------------------------------------------------------
    if (!selectedDate && !selectedStartDate) return ownershipGraph;

    // First pass: collect nodes reachable via dated edges only
    const datedEdges = allEdges.filter((e) => {
      const date = getDocumentForEdge(e, documents)?.effectiveDate ?? null;
      if (date === null) return false;
      if (selectedStartDate && date < selectedStartDate) return false;
      if (selectedDate && date > selectedDate) return false;
      return true;
    });

    const datedNodeIds = new Set<string>();
    for (const edge of datedEdges) {
      datedNodeIds.add(String(edge.fromNodeId));
      datedNodeIds.add(String(edge.toNodeId));
    }

    // Second pass: include null-doc edges (E5 consolidation) only if BOTH
    // endpoints are already reachable via dated edges
    const nullDateEdges = allEdges.filter(
      (e) =>
        (getDocumentForEdge(e, documents)?.effectiveDate ?? null) === null &&
        datedNodeIds.has(String(e.fromNodeId)) &&
        datedNodeIds.has(String(e.toNodeId))
    );

    const filteredEdgesList = [...datedEdges, ...nullDateEdges];

    const reachableNodeIds = new Set<string>();
    for (const edge of filteredEdgesList) {
      reachableNodeIds.add(String(edge.fromNodeId));
      reachableNodeIds.add(String(edge.toNodeId));
    }

    // Include establishment root nodes
    for (const [id] of Object.entries(ownershipGraph.nodes)) {
      const inbound = allEdges.filter(
        (e) =>
          String(e.toNodeId) === id &&
          (getDocumentForEdge(e, documents)?.effectiveDate ?? null) !== null
      );
      const outbound = allEdges.filter(
        (e) =>
          String(e.fromNodeId) === id &&
          (getDocumentForEdge(e, documents)?.effectiveDate ?? null) !== null
      );
      if (
        inbound.length === 0 &&
        outbound.some((e) => {
          const d = getDocumentForEdge(e, documents)?.effectiveDate ?? null;
          if (d === null) return false;
          if (selectedStartDate && d < selectedStartDate) return false;
          if (selectedDate && d > selectedDate) return false;
          return true;
        })
      ) {
        reachableNodeIds.add(id);
      }
    }

    const filteredNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(ownershipGraph.nodes)) {
      if (reachableNodeIds.has(id)) {
        filteredNodes[id] = node;
      }
    }

    const filteredEdges: Record<string, ConveyanceEdge> = {};
    for (const edge of filteredEdgesList) {
      filteredEdges[edge.id] = edge;
    }

    const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);

    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      adjacency,
      reverseAdjacency,
    };
  }, [
    ownershipGraph,
    documents,
    selectedDate,
    selectedStartDate,
    includedDocumentIds,
  ]);

  // Property filter — uses PostGIS spatial intersection on node polygons directly.
  // Returns intersecting node IDs — no names, no string matching.
  const { intersectingNodeIds } = useIntersectingProperties(
    filters.selectedLocations,
    timelineFilteredGraph,
    0.01
  );

  // Apply property filter: keep nodes whose ID is in the intersecting set,
  // plus all edges between them and their direct neighbours.
  const locationFilteredGraph = useMemo((): OwnershipGraph => {
    if (
      !filters.selectedLocations ||
      filters.selectedLocations.length === 0 ||
      intersectingNodeIds === null
    ) {
      return timelineFilteredGraph;
    }

    // Nodes: exactly the spatial match set — no neighbor expansion
    const filteredNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(timelineFilteredGraph.nodes)) {
      if (intersectingNodeIds.has(id)) {
        filteredNodes[id] = node;
      }
    }

    // Edges: kept only when BOTH endpoints are in the match set
    const filteredEdges: Record<string, ConveyanceEdge> = {};
    for (const [id, edge] of Object.entries(timelineFilteredGraph.edges)) {
      const fromId = edge.fromNodeId === null ? null : String(edge.fromNodeId);
      const toId = String(edge.toNodeId);
      if (
        fromId &&
        intersectingNodeIds.has(fromId) &&
        intersectingNodeIds.has(toId)
      ) {
        filteredEdges[id] = edge;
      }
    }

    const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      adjacency,
      reverseAdjacency,
    };
  }, [timelineFilteredGraph, filters.selectedLocations, intersectingNodeIds]);

  // Apply edge type filter
  const edgeTypeFilteredGraph = useMemo((): OwnershipGraph => {
    if (!filters.edgeTypeFilter || filters.edgeTypeFilter.length === 0) {
      return locationFilteredGraph;
    }
    const allowed = new Set(filters.edgeTypeFilter);
    const filteredEdges: Record<string, ConveyanceEdge> = {};
    for (const [id, edge] of Object.entries(locationFilteredGraph.edges)) {
      if (allowed.has(edge.eventType)) {
        filteredEdges[id] = edge;
      }
    }
    // Keep all nodes that are still referenced by remaining edges
    const referencedNodeIds = new Set<string>();
    for (const edge of Object.values(filteredEdges)) {
      if (edge.fromNodeId) referencedNodeIds.add(edge.fromNodeId);
      if (edge.toNodeId) referencedNodeIds.add(edge.toNodeId);
    }
    const filteredNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(locationFilteredGraph.nodes)) {
      if (referencedNodeIds.has(id)) filteredNodes[id] = node;
    }
    const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      adjacency,
      reverseAdjacency,
    };
  }, [locationFilteredGraph, filters.edgeTypeFilter]);

  // Filtered documents based on active filters (location + edge type)
  const filterScopedDocuments = useMemo(() => {
    const hasFilters =
      (filters.selectedLocations && filters.selectedLocations.length > 0) ||
      (filters.edgeTypeFilter && filters.edgeTypeFilter.length > 0);
    if (!hasFilters) return allSortedDocuments;

    const docs = groupEdgesByDocument(
      edgeTypeFilteredGraph,
      documents,
      {} as DocumentDetailMap
    );
    docs.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    return docs;
  }, [
    allSortedDocuments,
    edgeTypeFilteredGraph,
    documents,
    filters.selectedLocations,
    filters.edgeTypeFilter,
  ]);

  // sortedDocuments = the filter-scoped list used for runsheet display
  // allSortedDocuments = the full unfiltered list used for review-mode navigation
  const sortedDocuments = filterScopedDocuments;

  // Derive map geographies from node polygon data — one shape per unique geometry_name.
  // Compute current-owner node IDs (leaf nodes = no outbound edges as of filtered date)
  const currentOwnerNodeIds = useMemo(() => {
    if (!timelineFilteredGraph) return new Set<string>();
    const nodesWithOutbound = new Set<string>();
    for (const edge of getEdges(timelineFilteredGraph)) {
      nodesWithOutbound.add(String(edge.fromNodeId));
    }
    return new Set(
      getNodes(timelineFilteredGraph)
        .filter((n) => !nodesWithOutbound.has(String(n.id)))
        .map((n) => String(n.id))
    );
  }, [timelineFilteredGraph]);

  // All geographies with any non-zero ownership — needed by severance visualization
  // (which renders the "before" parent shape even after it's been fully conveyed away).
  const allGeographies = useMemo((): MapGeography[] => {
    const geoByName = new Map<string, GeoJSON.Geometry>();
    for (const node of Object.values(locationFilteredGraph.nodes)) {
      const polygon = node.property.volume.polygon;
      const name = node.property.volume.geometryName;
      if (!polygon || !name || geoByName.has(name)) continue;
      if (node.interests.some((i) => i.fractionF > 0)) {
        geoByName.set(name, polygon as GeoJSON.Geometry);
      }
    }
    let idCounter = 1;
    return [...geoByName].map(([name, polygon]) => ({
      id: idCounter++,
      referenceLocationName: name,
      searchText: name,
      geometryJson: polygon,
    }));
  }, [locationFilteredGraph]);

  // Subset of geographies for the ownership map — only tracts with a current (leaf) owner.
  const mapGeographies = useMemo((): MapGeography[] => {
    const currentGeoNames = new Set<string>();
    for (const node of Object.values(locationFilteredGraph.nodes)) {
      if (!currentOwnerNodeIds.has(String(node.id))) continue;
      if (!node.interests.some((i) => i.fractionF > 0)) continue;
      const name = node.property.volume.geometryName;
      if (name) currentGeoNames.add(name);
    }
    return allGeographies.filter((g) =>
      currentGeoNames.has(g.referenceLocationName)
    );
  }, [locationFilteredGraph, currentOwnerNodeIds, allGeographies]);

  // Subject asset node IDs — the entity's current leaf nodes from the backend
  const subjectAssetNodeIdSet = useMemo((): Set<string> => {
    return new Set((chainData.subjectAssetNodeIds ?? []).map(String));
  }, [chainData.subjectAssetNodeIds]);

  // Filter locationFilteredGraph to only subject asset nodes (for ownership table)
  const subjectAssetGraph = useMemo((): OwnershipGraph => {
    const filteredNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(locationFilteredGraph.nodes)) {
      if (subjectAssetNodeIdSet.has(id)) {
        filteredNodes[id] = node;
      }
    }
    const filteredEdges: Record<string, ConveyanceEdge> = {};
    for (const [id, edge] of Object.entries(locationFilteredGraph.edges)) {
      const fromId = edge.fromNodeId === null ? null : String(edge.fromNodeId);
      const toId = String(edge.toNodeId);
      if (
        subjectAssetNodeIdSet.has(toId) &&
        (fromId === null || subjectAssetNodeIdSet.has(fromId))
      ) {
        filteredEdges[id] = edge;
      }
    }
    const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      adjacency,
      reverseAdjacency,
    };
  }, [locationFilteredGraph, subjectAssetNodeIdSet]);

  // Scope graph to union of all path sets to review (when present)
  const pathSetsSubgraph = useMemo((): OwnershipGraph | null => {
    const paths = chainData.pathsToReview;
    if (!paths || paths.length === 0) return null;

    const allNodeIds = new Set<string>();
    const allEdgeIds = new Set<string>();
    for (const path of paths) {
      for (const id of path.nodeIds) allNodeIds.add(String(id));
      for (const id of path.edgeIds) allEdgeIds.add(String(id));
    }

    for (const [id, edge] of Object.entries(locationFilteredGraph.edges)) {
      if (allEdgeIds.has(id)) {
        if (edge.fromNodeId !== null) allNodeIds.add(String(edge.fromNodeId));
        allNodeIds.add(String(edge.toNodeId));
      }
    }

    const filteredNodes: Record<string, OwnershipNode> = {};
    for (const [id, node] of Object.entries(locationFilteredGraph.nodes)) {
      if (allNodeIds.has(id)) filteredNodes[id] = node;
    }

    const filteredEdges: Record<string, ConveyanceEdge> = {};
    for (const [id, edge] of Object.entries(locationFilteredGraph.edges)) {
      if (
        allEdgeIds.has(id) &&
        filteredNodes[String(edge.fromNodeId)] !== undefined &&
        filteredNodes[String(edge.toNodeId)] !== undefined
      ) {
        filteredEdges[id] = edge;
      }
    }

    const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      adjacency,
      reverseAdjacency,
    };
  }, [chainData.pathsToReview, locationFilteredGraph]);

  // Memoize chainData so TitleValidatorGraph doesn't get a new object on every render
  const graphChainData = useMemo(
    () => ({
      ...chainData,
      graph: pathSetsSubgraph ?? locationFilteredGraph,
    }),
    [chainData, locationFilteredGraph, pathSetsSubgraph]
  );

  // Clear selection if filtered-out node/edge is selected
  const prevSelectedDate = useRef(selectedDate);
  const prevSelectedStartDate = useRef(selectedStartDate);
  const dateChanged =
    prevSelectedDate.current !== selectedDate ||
    prevSelectedStartDate.current !== selectedStartDate;
  if (dateChanged) {
    prevSelectedDate.current = selectedDate;
    prevSelectedStartDate.current = selectedStartDate;
    // Always dismiss toolbar when timeline changes
    clearToolbarRef.current?.();
    if (selectedNodeId && timelineFilteredGraph) {
      const nodeExists = selectedNodeId in timelineFilteredGraph.nodes;
      if (!nodeExists) {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
      }
    }
  }

  // Handle graph conveyance card click — scroll to document in runsheet
  const handleEdgeClick = useCallback(
    (_conveyanceId: number | null, documentId: number) => {
      setSelectedEdgeId(null);
      setSelectedNodeId(null);
      setSelectedTransactionId(null);
      setSelectedDocumentId(documentId);
      setActiveReviewPath(null);
    },
    []
  );

  // Handle graph edge-level click (ownership graph)
  const handleEdgeLevelClick = useCallback((edgeId: string) => {
    setSelectedEdgeId(edgeId);
    setExpandConvergent(true);
    setSelectedDocumentId(null);
    setSelectedNodeId(null);
    setActiveReviewPath(null);
  }, []);

  // Handle runsheet edge row click — select single edge only (no convergent expansion)
  const handleRunsheetEdgeClick = useCallback((edgeId: string) => {
    setSelectedEdgeId((prev) => (prev === edgeId ? null : edgeId));
    setExpandConvergent(false);
    setSelectedDocumentId(null);
    setSelectedTransactionId(null);
    setSelectedNodeId(null);
    setActiveReviewPath(null);
    clearToolbarRef.current?.();
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSelectedDocumentId(null);
    setSelectedTransactionId(null);
    setActiveReviewPath(null);
    clearToolbarRef.current?.();
  }, []);

  const [citationNodeId, setCitationNodeId] = useState<string | null>(null);

  // Paths-to-review panel — active path selection
  const [activeReviewPath, setActiveReviewPath] = useState<PathToReview | null>(
    null
  );
  const [pathsDropdownOpen, setPathsDropdownOpen] = useState(false);
  const pathsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pathsDropdownOpen) return;
    function handleOutsideClick(e: MouseEvent) {
      if (
        pathsDropdownRef.current &&
        !pathsDropdownRef.current.contains(e.target as Node)
      ) {
        setPathsDropdownOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setPathsDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [pathsDropdownOpen]);

  const handleSelectReviewPath = useCallback((path: PathToReview) => {
    setActiveReviewPath((prev) => {
      return prev?.label === path.label && prev?.reason === path.reason
        ? null
        : path;
    });
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSelectedDocumentId(null);
    setSelectedTransactionId(null);
    clearToolbarRef.current?.();
  }, []);

  // Handle table row click — toggle path highlighting, update lineage node
  const handleNodeRowClick = useCallback((nodeId: string) => {
    setSelectedNodeId((prev) => (prev === nodeId ? null : nodeId));
    setCitationNodeId((prev) => (prev === nodeId ? null : nodeId));
    setSelectedEdgeId(null);
    setSelectedDocumentId(null);
    setSelectedTransactionId(null);
    setActiveReviewPath(null);
    clearToolbarRef.current?.();
  }, []);

  // Asset preview handlers — disabled for now

  // Handle document click from runsheet — highlight all edges for that document
  const handleDocumentClick = useCallback((documentId: number) => {
    setSelectedDocumentId((prev) => (prev === documentId ? null : documentId));
    setSelectedTransactionId(null);
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    setActiveReviewPath(null);
    clearToolbarRef.current?.();
  }, []);

  const handleTransactionClick = useCallback((transactionId: number | null) => {
    setSelectedTransactionId((prev) => {
      return prev === transactionId ? null : transactionId;
    });
    setSelectedDocumentId(null);
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    setActiveReviewPath(null);
    clearToolbarRef.current?.();
  }, []);

  const handleEnterDocReview = useCallback(
    (documentId?: number) => {
      // Start at the oldest doc (last in newest-first array), or the specified doc
      const idx = documentId
        ? sortedDocuments.findIndex((d) => d.documentId === documentId)
        : sortedDocuments.length - 1;
      const resolvedIdx = idx >= 0 ? idx : sortedDocuments.length - 1;
      setDocReviewMode(true);
      setReviewDocIndex(resolvedIdx);
      setSelectedDocumentId(sortedDocuments[resolvedIdx]?.documentId ?? null);
      setSelectedTransactionId(null);
      setSelectedEdgeId(null);
      setSelectedNodeId(null);
      clearToolbarRef.current?.();
    },
    [sortedDocuments]
  );

  const handleDocReviewBack = useCallback(() => {
    // Keep the current document selected so the runsheet scrolls to it
    if (reviewDocIndex !== null && sortedDocuments[reviewDocIndex]) {
      setSelectedDocumentId(sortedDocuments[reviewDocIndex].documentId);
    }
    setDocReviewMode(false);
    setReviewDocIndex(null);
  }, [reviewDocIndex, sortedDocuments]);

  // Toolbar action handlers — memoized so TitleValidatorGraph doesn't re-render
  const handleTracePath = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  }, []);

  // Drag-to-resize handler for panel dividers
  const startDrag = useCallback(
    (
      axis: 'horizontal' | 'vertical',
      containerRef: React.RefObject<HTMLDivElement>,
      setter: (ratio: number) => void
    ) => {
      const container = containerRef.current;
      if (!container) return;

      const onMouseMove = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        const ratio =
          axis === 'horizontal'
            ? (e.clientX - rect.left) / rect.width
            : (e.clientY - rect.top) / rect.height;
        setter(Math.max(0.15, Math.min(0.85, ratio)));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor =
        axis === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    []
  );

  // Compute highlighted path — edge click highlights the edge + its 2 nodes
  // (or all convergent siblings if fan-in), node row click traces full ancestry
  const highlightedPath = useMemo(() => {
    const allEdges = getEdges(timelineFilteredGraph);

    // Document selection — highlight all edges for this document + their nodes
    if (selectedDocumentId !== null) {
      const docEdges = getEdges(timelineFilteredGraph).filter(
        (e) => e.documentId === selectedDocumentId
      );
      const nodeIds = new Set<string>();
      const edgeIds = new Set<string>();
      for (const edge of docEdges) {
        nodeIds.add(String(edge.fromNodeId));
        nodeIds.add(String(edge.toNodeId));
        edgeIds.add(String(edge.id));
      }
      return {
        nodeIds: Array.from(nodeIds),
        edgeIds: Array.from(edgeIds),
        documentId: selectedDocumentId,
      };
    }

    // Transaction selection — highlight only edges for this transaction + their output nodes
    if (selectedTransactionId !== null) {
      const txnEdges = getEdges(timelineFilteredGraph).filter(
        (e) => e.transactionId === selectedTransactionId
      );
      const nodeIds = new Set<string>();
      const edgeIds = new Set<string>();
      for (const edge of txnEdges) {
        nodeIds.add(String(edge.fromNodeId));
        nodeIds.add(String(edge.toNodeId));
        edgeIds.add(String(edge.id));
      }
      return {
        nodeIds: Array.from(nodeIds),
        edgeIds: Array.from(edgeIds),
        transactionId: selectedTransactionId,
        documentId: txnEdges[0]?.documentId ?? null,
      };
    }

    if (selectedEdgeId && timelineFilteredGraph) {
      const edge = timelineFilteredGraph.edges[selectedEdgeId];
      if (edge) {
        // Expand to convergent siblings only when triggered from graph badge
        if (expandConvergent) {
          const siblings = allEdges.filter(
            (e) =>
              e.toNodeId === edge.toNodeId && e.documentId === edge.documentId
          );
          if (siblings.length > 1) {
            const nodeIds = new Set<string>();
            nodeIds.add(edge.toNodeId);
            for (const s of siblings) {
              if (s.fromNodeId) nodeIds.add(s.fromNodeId);
            }
            return {
              nodeIds: Array.from(nodeIds),
              edgeIds: siblings.map((s) => s.id),
            };
          }
        }
        // Expand to conveyed+retained pair (same from_node, document, event_type, to_node)
        const retainedPair = allEdges.find(
          (e) =>
            e.id !== edge.id &&
            e.fromNodeId === edge.fromNodeId &&
            e.toNodeId === edge.toNodeId &&
            e.documentId === edge.documentId &&
            e.eventType === edge.eventType &&
            !!e.params.retained !== !!edge.params.retained
        );
        if (retainedPair) {
          return {
            nodeIds: [edge.fromNodeId, edge.toNodeId].filter(
              (id): id is string => id !== null
            ),
            edgeIds: [edge.id, retainedPair.id],
            documentId: edge.documentId,
            transactionId: edge.transactionId,
          };
        }
        return {
          nodeIds: [edge.fromNodeId, edge.toNodeId].filter(
            (id): id is string => id !== null
          ),
          edgeIds: [edge.id],
          documentId: edge.documentId,
          transactionId: edge.transactionId,
        };
      }
    }
    if (selectedNodeId && timelineFilteredGraph) {
      return tracePathToRoot(selectedNodeId, timelineFilteredGraph);
    }
    if (activeReviewPath) {
      return {
        nodeIds: activeReviewPath.nodeIds.map(String),
        edgeIds: activeReviewPath.edgeIds.map(String),
      };
    }
    return null;
  }, [
    selectedDocumentId,
    selectedTransactionId,
    selectedEdgeId,
    selectedNodeId,
    expandConvergent,
    timelineFilteredGraph,
    activeReviewPath,
  ]);

  // Edge IDs selected in the runsheet — includes convergent siblings
  const selectedEdgeIds = useMemo(() => {
    if (!highlightedPath) return new Set<string>();
    if (
      selectedEdgeId ||
      selectedDocumentId !== null ||
      selectedTransactionId !== null
    ) {
      return new Set(highlightedPath.edgeIds);
    }
    return new Set<string>();
  }, [
    selectedEdgeId,
    selectedDocumentId,
    selectedTransactionId,
    highlightedPath,
  ]);

  // Graph scoped to path trace + active filters — used by the runsheet
  const runsheetGraph = useMemo((): OwnershipGraph => {
    // Start from edge-type-filtered graph (already has location + edge type filters)
    let graph = edgeTypeFilteredGraph;

    // If a node path is being traced or a review path is active, scope to only edges in the path
    if (
      (selectedNodeId || activeReviewPath) &&
      highlightedPath &&
      highlightedPath.edgeIds.length > 0
    ) {
      const pathEdgeIds = new Set(highlightedPath.edgeIds.map(String));
      const filteredEdges: Record<string, ConveyanceEdge> = {};
      for (const [id, edge] of Object.entries(graph.edges)) {
        if (pathEdgeIds.has(id)) {
          filteredEdges[id] = edge;
        }
      }
      const referencedNodeIds = new Set<string>();
      for (const edge of Object.values(filteredEdges)) {
        if (edge.fromNodeId) referencedNodeIds.add(String(edge.fromNodeId));
        if (edge.toNodeId) referencedNodeIds.add(String(edge.toNodeId));
      }
      const filteredNodes: Record<string, OwnershipNode> = {};
      for (const [id, node] of Object.entries(graph.nodes)) {
        if (referencedNodeIds.has(id)) filteredNodes[id] = node;
      }
      const { adjacency, reverseAdjacency } = buildAdjacency(filteredEdges);
      graph = {
        nodes: filteredNodes,
        edges: filteredEdges,
        adjacency,
        reverseAdjacency,
      };
    }

    return graph;
  }, [
    edgeTypeFilteredGraph,
    selectedNodeId,
    activeReviewPath,
    highlightedPath,
  ]);

  // Total document count (unfiltered) for the scoped indicator
  const totalDocumentCount = allSortedDocuments.length;

  // Selection context banner — path set for node selection, operation for edge selection
  const selectionBanner = useMemo<SelectionBanner | null>(() => {
    if (!timelineFilteredGraph || !highlightedPath) return null;

    const allEdges = getEdges(timelineFilteredGraph);

    // Document selection -> document banner
    if (selectedDocumentId !== null) {
      const docEdges = getEdges(timelineFilteredGraph).filter(
        (e) => e.documentId === selectedDocumentId
      );
      const effectiveDates = docEdges
        .map((e) => getDocumentForEdge(e, documents)?.effectiveDate ?? null)
        .filter((d): d is string => d !== null)
        .sort();
      const parties = new Set<string>();
      for (const edge of docEdges) {
        const from = timelineFilteredGraph.nodes[String(edge.fromNodeId)];
        const to = timelineFilteredGraph.nodes[String(edge.toNodeId)];
        if (from) parties.add(from.entity.entityName);
        if (to) parties.add(to.entity.entityName);
      }
      const docMeta = sortedDocuments.find(
        (d) => d.documentId === selectedDocumentId
      );
      return {
        type: 'document' as const,
        documentId: selectedDocumentId,
        documentTitle:
          docMeta?.documentTitle ?? `Document #${selectedDocumentId}`,
        operationCount: docEdges.length,
        partyCount: parties.size,
        effectiveDate: effectiveDates.length > 0 ? effectiveDates[0] : null,
      };
    }

    // Edge selection -> operation banner
    if (selectedEdgeId) {
      const edge = timelineFilteredGraph.edges[selectedEdgeId];
      if (!edge) return null;
      const fromNode =
        edge.fromNodeId !== null
          ? timelineFilteredGraph.nodes[edge.fromNodeId]
          : undefined;
      const toNode =
        edge.toNodeId !== null
          ? timelineFilteredGraph.nodes[edge.toNodeId]
          : undefined;
      return {
        type: 'operation' as const,
        edgeTypeLabel: getEdgeTypeLabel(
          edge.eventType,
          edge.params.retained,
          fromNode,
          toNode
        ),
        fromEntityName:
          fromNode?.entity.entityName ?? edge.fromNodeId ?? 'Unknown',
        toEntityName: toNode?.entity.entityName ?? edge.toNodeId ?? 'Unknown',
        effectiveDate: getDocumentForEdge(edge, documents)?.effectiveDate ?? '',
        edgeCount: highlightedPath.edgeIds.length,
      };
    }

    // Active review path -> path set banner
    if (activeReviewPath) {
      return {
        type: 'pathSet' as const,
        entityName: activeReviewPath.label,
        geometry: '',
        effectiveDate: null,
        pathSetDocumentCount: new Set(
          allEdges
            .filter((e) => new Set(highlightedPath.edgeIds).has(String(e.id)))
            .map((e) => e.documentId)
            .filter((id): id is number => id !== null)
        ).size,
        establishmentName: null,
        isCurrentOwner: false,
        isEstablishment: false,
      };
    }

    // Node selection -> path set banner
    if (selectedNodeId) {
      const node = timelineFilteredGraph.nodes[selectedNodeId];
      if (!node) return null;

      // Find effective date from the inbound edge that created this node
      const inboundEdge = allEdges.find((e) => e.toNodeId === selectedNodeId);

      // Find establishment (root) node in the path set
      const pathNodeIds = new Set(highlightedPath.nodeIds);
      const nodesWithInbound = new Set(
        allEdges
          .filter((e) => pathNodeIds.has(e.toNodeId))
          .map((e) => e.toNodeId)
      );
      const rootNode = getNodes(timelineFilteredGraph).find(
        (n) => pathNodeIds.has(n.id) && !nodesWithInbound.has(n.id)
      );

      // Determine if this is an establishment node (root of the graph — no inbound edges)
      const nodeHasInbound = allEdges.some(
        (e) => e.toNodeId === selectedNodeId
      );

      return {
        type: 'pathSet' as const,
        entityName: node.entity.entityName,
        geometry: node.property.volume.geometryName ?? '',
        effectiveDate: inboundEdge
          ? (getDocumentForEdge(inboundEdge, documents)?.effectiveDate ?? null)
          : null,
        pathSetDocumentCount: new Set(
          allEdges
            .filter((e) => new Set(highlightedPath.edgeIds).has(String(e.id)))
            .map((e) => e.documentId)
            .filter((id): id is number => id !== null)
        ).size,
        establishmentName:
          rootNode && rootNode.id !== selectedNodeId
            ? rootNode.entity.entityName
            : null,
        isCurrentOwner: currentOwnerNodeIds.has(node.id),
        isEstablishment: !nodeHasInbound,
      };
    }

    return null;
  }, [
    selectedDocumentId,
    selectedNodeId,
    selectedEdgeId,
    activeReviewPath,
    timelineFilteredGraph,
    highlightedPath,
    currentOwnerNodeIds,
    sortedDocuments,
    documents,
  ]);

  // Resolve the selected document context for the "Document Review" mode button.
  // If a transaction is selected, find which document it belongs to.
  const selectedDocIdForReview = useMemo(() => {
    if (selectedDocumentId !== null) return selectedDocumentId;
    if (selectedTransactionId !== null) {
      const doc = sortedDocuments.find((d) =>
        d.transactions.some((t) => t.transactionId === selectedTransactionId)
      );
      return doc?.documentId ?? null;
    }
    return null;
  }, [selectedDocumentId, selectedTransactionId, sortedDocuments]);

  const renderSelectionBannerContent = () => {
    if (!selectionBanner) return null;
    if (selectionBanner.type === 'pathSet') {
      return (
        <>
          <div className={styles.selectionBannerTopRow}>
            <span className={styles.selectionBannerLabel}>
              Showing path set for
            </span>
            <span
              className={styles.selectionBannerEntity}
              title={selectionBanner.entityName}
            >
              {selectionBanner.entityName}
            </span>
            {selectionBanner.isEstablishment && (
              <Tag type="blue" size="sm">
                Establishment
              </Tag>
            )}
            {selectionBanner.isCurrentOwner && (
              <Tag type="blue" size="sm">
                Current Owner
              </Tag>
            )}
          </div>
          <div className={styles.selectionBannerBottomRow}>
            <span
              className={styles.selectionBannerDetail}
              title={selectionBanner.geometry}
            >
              {selectionBanner.geometry}
            </span>
            {selectionBanner.effectiveDate && (
              <span className={styles.selectionBannerDetail}>
                {selectionBanner.effectiveDate}
              </span>
            )}
            <span className={styles.selectionBannerDetail}>
              {selectionBanner.pathSetDocumentCount}{' '}
              {selectionBanner.pathSetDocumentCount === 1
                ? 'document'
                : 'documents'}
            </span>
            {selectionBanner.establishmentName && (
              <span className={styles.selectionBannerDetail}>
                Back to: {selectionBanner.establishmentName}
              </span>
            )}
          </div>
        </>
      );
    }
    if (selectionBanner.type === 'document') {
      return (
        <>
          <div className={styles.selectionBannerTopRow}>
            <span className={styles.selectionBannerLabel}>Showing</span>
            <span className={styles.selectionBannerEntity}>
              {selectionBanner.documentTitle ??
                `Document #${selectionBanner.documentId}`}
            </span>
          </div>
          <div className={styles.selectionBannerBottomRow}>
            <span className={styles.selectionBannerDetail}>
              {selectionBanner.operationCount} operations
            </span>
            <span className={styles.selectionBannerDetail}>
              {selectionBanner.partyCount} parties
            </span>
            {selectionBanner.effectiveDate && (
              <span className={styles.selectionBannerDetail}>
                Eff. {selectionBanner.effectiveDate}
              </span>
            )}
          </div>
        </>
      );
    }
    return (
      <>
        <div className={styles.selectionBannerTopRow}>
          <span className={styles.selectionBannerLabel}>Showing operation</span>
          <span className={styles.selectionBannerEntity}>
            {selectionBanner.edgeTypeLabel}
          </span>
        </div>
        <div className={styles.selectionBannerBottomRow}>
          <span className={styles.selectionBannerDetail}>
            {selectionBanner.fromEntityName} &rarr;{' '}
            {selectionBanner.toEntityName}
          </span>
          <span className={styles.selectionBannerDetail}>
            {selectionBanner.effectiveDate}
          </span>
          {selectionBanner.edgeCount > 1 && (
            <span className={styles.selectionBannerDetail}>
              {selectionBanner.edgeCount} convergent edges
            </span>
          )}
        </div>
      </>
    );
  };

  return (
    <div className={styles.resultsContainer}>
      {/* Combined Header: Back + Timeline (left) + Metadata (right) */}
      <div className={styles.headerBar}>
        {/* Sidebar collapse/expand toggle */}
        {onToggleSidebar && (
          <FloatingTooltip
            content={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            placement="bottom"
          >
            <button
              type="button"
              className={styles.backToOverview}
              onClick={() => {
                onToggleSidebar();
                // Blur the button to dismiss the tooltip
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              {sidebarCollapsed ? (
                <OpenPanelLeft style={{ width: 18, height: 18 }} />
              ) : (
                <OpenPanelRight style={{ width: 18, height: 18 }} />
              )}
            </button>
          </FloatingTooltip>
        )}
        {onToggleSidebar && <div className={styles.headerDivider} />}

        <div className={styles.headerMetaRight}>
          {/* Mode switcher: icon toggle group */}
          <div className={styles.modeSwitcher}>
            <Button
              hasIconOnly
              renderIcon={Time}
              iconDescription=""
              size="sm"
              kind={!docReviewMode ? 'primary' : 'ghost'}
              onClick={() => {
                if (docReviewMode) handleDocReviewBack();
              }}
            />
            <Button
              hasIconOnly
              renderIcon={DocumentMultiple01}
              iconDescription=""
              size="sm"
              kind={docReviewMode ? 'primary' : 'ghost'}
              onClick={() => {
                if (!docReviewMode)
                  handleEnterDocReview(selectedDocIdForReview ?? undefined);
              }}
            />
          </div>
          {/* Layout + Filters menus */}
          <LayoutMenu
            layoutSettings={layoutSettings}
            onLayoutChange={onLayoutChange}
          />
          <FiltersMenu
            filters={filters}
            uniqueLocations={uniqueLocations}
            onFiltersChange={onFiltersChange}
            onResetFilters={onResetFilters}
          />
        </div>
        <div className={styles.headerDivider} />
        {/* Metadata info bar */}
        <div className={styles.headerMetaBar}>
          <div className={styles.headerMetaSection}>
            <span className={styles.headerMetaLabel}>
              TITLE CHAIN VALIDATION
            </span>
          </div>
          <div className={styles.headerDivider} />
          <div className={styles.headerMetaSection}>
            <span className={styles.headerMetaLabel}>ESTABLISHMENTS</span>
            <span className={styles.headerMetaValue}>
              {uniqueEstablishmentNames.length === 0
                ? '\u2014'
                : uniqueEstablishmentNames.slice(0, 2).join(', ') +
                  (uniqueEstablishmentNames.length > 2
                    ? ` +${uniqueEstablishmentNames.length - 2}`
                    : '')}
            </span>
          </div>
          <span className={styles.headerMetaArrow}>{'\u2192'}</span>
          <div className={styles.headerMetaSection}>
            <span className={styles.headerMetaLabel}>SUBJECT DOCUMENT</span>
            {documentHref ? (
              <a href={documentHref} className={styles.headerMetaLink}>
                {documentTitle
                  ? `${documentTitle}${
                      documentId ? ` \u00B7 ${documentId}` : ''
                    }`
                  : documentId
                    ? `Document ${documentId}`
                    : '\u2014'}
              </a>
            ) : (
              <span className={styles.headerMetaValue}>
                {documentTitle
                  ? `${documentTitle}${
                      documentId ? ` \u00B7 ${documentId}` : ''
                    }`
                  : documentId
                    ? `Document ${documentId}`
                    : '\u2014'}
              </span>
            )}
          </div>
          {targetEntityName && (
            <>
              <div className={styles.headerDivider} />
              <div className={styles.headerMetaSection}>
                <span className={styles.headerMetaLabel}>SUBJECT ENTITY</span>
                <span className={styles.headerMetaValueBold}>
                  {targetEntityName}
                </span>
              </div>
            </>
          )}
          {reconcilerUrl && (
            <>
              <div className={styles.headerDivider} />
              <Button
                kind="tertiary"
                href={reconcilerUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Reconciler
              </Button>
            </>
          )}
          {(chainData.pathsToReview?.length ?? 0) > 0 && (
            <>
              <div className={styles.headerDivider} />
              <div
                ref={pathsDropdownRef}
                className={styles.pathsToReviewDropdown}
              >
                <button
                  type="button"
                  className={styles.pathsToReviewTrigger}
                  onClick={() => setPathsDropdownOpen((o) => !o)}
                >
                  <span className={styles.pathsToReviewTriggerText}>
                    {activeReviewPath
                      ? activeReviewPath.label
                      : 'Select a path set to review...'}
                  </span>
                  <ChevronDown className={styles.pathsToReviewCaret} />
                </button>
                {pathsDropdownOpen && (
                  <div className={styles.pathsToReviewPanel}>
                    <div className={styles.pathsToReviewPanelHeader}>
                      <span className={styles.pathsToReviewPanelTitle}>
                        PATH SETS TO REVIEW
                      </span>
                      <span className={styles.pathsToReviewCount}>
                        {chainData.pathsToReview?.length ?? 0}
                      </span>
                    </div>
                    {chainData.pathsToReview?.map((path, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={classNames(styles.pathsToReviewOption, {
                          [styles.pathsToReviewOptionActive]:
                            activeReviewPath?.nodeIds.join(',') ===
                            path.nodeIds.join(','),
                        })}
                        onClick={() => {
                          handleSelectReviewPath(path);
                          setPathsDropdownOpen(false);
                        }}
                      >
                        {path.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Full-width selection banner — above both graph and table panels */}
      {selectionBanner && (
        <div className={styles.selectionBanner}>
          <div className={styles.selectionBannerContent}>
            {renderSelectionBannerContent()}
          </div>
          <button
            type="button"
            className={styles.selectionBannerClose}
            onClick={handleClearSelection}
            title={
              selectionBanner.type === 'pathSet'
                ? activeReviewPath
                  ? 'Deselect path'
                  : 'Deselect node'
                : selectionBanner.type === 'document'
                  ? 'Deselect document'
                  : 'Deselect operation'
            }
            aria-label={
              selectionBanner.type === 'pathSet'
                ? activeReviewPath
                  ? 'Deselect path'
                  : 'Deselect node'
                : selectionBanner.type === 'document'
                  ? 'Deselect document'
                  : 'Deselect operation'
            }
          >
            <Close />
          </button>
        </div>
      )}

      {/* Dual View: Graph + Table */}
      <div
        ref={viewPanelsRef}
        className={`${styles.viewPanels} ${
          layoutSettings.layoutMode === 'stacked'
            ? styles.stacked
            : styles.sideBySide
        }`}
      >
        {/* Graph Panel */}
        {layoutSettings.showGraph && (
          <div
            className={styles.graphPanel}
            style={
              horizontalSplit !== null && layoutSettings.showTable
                ? { flex: `0 0 ${horizontalSplit * 100}%` }
                : undefined
            }
          >
            <Suspense
              fallback={
                <div className={styles.loadingGraph}>
                  <InlineLoading description="Loading graph..." />
                </div>
              }
            >
              <TitleValidatorGraph
                chainData={graphChainData}
                hasNothingToGraph={hasNothingToGraph}
                filters={filters}
                onEdgeClick={handleEdgeClick}
                onEdgeLevelClick={handleEdgeLevelClick}
                onOwnershipNodeClick={handleNodeRowClick}
                onClearSelection={handleClearSelection}
                graphDirection={layoutSettings.graphDirection}
                onGraphDirectionChange={(dir) =>
                  onLayoutChange({ graphDirection: dir })
                }
                consolidation={layoutSettings.consolidation}
                onConsolidationChange={(level) =>
                  onLayoutChange({ consolidation: level })
                }
                highlightedPath={highlightedPath}
                highlightOnly={!!activeReviewPath}
                currentOwnerNodeIds={currentOwnerNodeIds}
                onTracePath={handleTracePath}
                onSelectDocument={handleDocumentClick}
                onSelectTransaction={handleTransactionClick}
                onSetAsOfDate={setSelectedDate}
                clearToolbarRef={clearToolbarRef}
              />
            </Suspense>
          </div>
        )}

        {/* Drag handle — graph <-> table (axis depends on layout mode) */}
        {layoutSettings.showGraph && layoutSettings.showTable && (
          <div
            className={
              layoutSettings.layoutMode === 'stacked'
                ? styles.dragHandleHorizontal
                : styles.dragHandleVertical
            }
            onMouseDown={() =>
              startDrag(
                layoutSettings.layoutMode === 'stacked'
                  ? 'vertical'
                  : 'horizontal',
                viewPanelsRef,
                setHorizontalSplit
              )
            }
          />
        )}

        {/* Table Panel - Ownership Entries Table + Runsheet */}
        {layoutSettings.showTable && timelineFilteredGraph && (
          <div
            ref={tablePanelRef}
            className={styles.tablePanel}
            style={
              horizontalSplit !== null && layoutSettings.showGraph
                ? { flex: `0 0 ${(1 - horizontalSplit) * 100}%` }
                : undefined
            }
          >
            {(layoutSettings.showCurrentOwnership ||
              layoutSettings.showMap) && (
              <div
                className={styles.tablePanelUpper}
                style={
                  verticalSplit !== null && layoutSettings.showRunsheet
                    ? { flex: `0 0 ${verticalSplit * 100}%` }
                    : undefined
                }
              >
                <div className={styles.ownershipHeader}>
                  <span className={styles.ownershipHeaderTitle}>
                    {subjectAssetNodeIdSet.size > 1
                      ? 'Subject Assets'
                      : 'Subject Asset'}
                  </span>
                  <span className={styles.ownershipHeaderDate}>
                    {docReviewMode && reviewDocIndex !== null
                      ? `As of Document ${
                          sortedDocuments[reviewDocIndex]?.documentTitle ??
                          `#${sortedDocuments[reviewDocIndex]?.documentId}`
                        }`
                      : (() => {
                          const subjectDoc =
                            documentId !== null
                              ? documents[String(documentId)]
                              : undefined;
                          const title =
                            documentTitle ??
                            (documentId !== null
                              ? `Document #${documentId}`
                              : null);
                          const effDate = subjectDoc?.effectiveDate
                            ? new Date(
                                subjectDoc.effectiveDate + 'T00:00:00'
                              ).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : null;
                          if (title && effDate)
                            return `As of ${title} (Effective Date: ${effDate})`;
                          if (title) return `As of ${title}`;
                          const dateOpts: Intl.DateTimeFormatOptions = {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          };
                          if (!selectedDate) {
                            if (!timelineRange) return 'As of Present';
                            return `As of ${new Date(
                              timelineRange.maxDate + 'T00:00:00'
                            ).toLocaleDateString('en-US', dateOpts)} (Latest)`;
                          }
                          const formatted = new Date(
                            selectedDate + 'T00:00:00'
                          ).toLocaleDateString('en-US', dateOpts);
                          if (
                            timelineRange &&
                            selectedDate === timelineRange.minDate
                          )
                            return `As of ${formatted} (Establishment)`;
                          if (
                            timelineRange &&
                            selectedDate === timelineRange.maxDate
                          )
                            return `As of ${formatted} (Latest)`;
                          return `As of ${formatted}`;
                        })()}
                  </span>
                  {/* <span className={styles.ownershipHeaderStat}>
                    {currentOwnerNodeIds.size} Owner
                    {currentOwnerNodeIds.size !== 1 ? 's' : ''} &middot;{' '}
                    {filteredEdgeCount} Edge
                    {filteredEdgeCount !== 1 ? 's' : ''}
                  </span> */}
                  <div className={styles.ownershipHeaderActions}>
                    <FloatingTooltip
                      content={
                        layoutSettings.showCurrentOwnership
                          ? 'Hide Ownership Table'
                          : 'Show Ownership Table'
                      }
                      placement="bottom"
                      asChild
                    >
                      <Button
                        hasIconOnly
                        renderIcon={DataTable}
                        iconDescription="Ownership Table"
                        size="sm"
                        kind={
                          layoutSettings.showCurrentOwnership
                            ? 'primary'
                            : 'ghost'
                        }
                        onClick={() => {
                          if (
                            layoutSettings.showCurrentOwnership &&
                            !layoutSettings.showMap
                          )
                            return;
                          onLayoutChange({
                            showCurrentOwnership:
                              !layoutSettings.showCurrentOwnership,
                          });
                        }}
                      />
                    </FloatingTooltip>
                    <FloatingTooltip
                      content={
                        showLineage
                          ? 'Hide Ownership Lineage'
                          : 'Show Ownership Lineage'
                      }
                      placement="bottom"
                      asChild
                    >
                      <Button
                        hasIconOnly
                        renderIcon={DataConnected}
                        iconDescription="Ownership Lineage"
                        size="sm"
                        kind={showLineage ? 'primary' : 'ghost'}
                        onClick={() => setShowLineage((v) => !v)}
                      />
                    </FloatingTooltip>
                    <FloatingTooltip
                      content={
                        layoutSettings.showMap
                          ? 'Hide Ownership Map'
                          : 'Show Ownership Map'
                      }
                      placement="bottom"
                      asChild
                    >
                      <Button
                        hasIconOnly
                        renderIcon={MapBoundary}
                        iconDescription="Ownership Map"
                        size="sm"
                        kind={layoutSettings.showMap ? 'primary' : 'ghost'}
                        onClick={() => {
                          if (
                            layoutSettings.showMap &&
                            !layoutSettings.showCurrentOwnership
                          )
                            return;
                          onLayoutChange({
                            showMap: !layoutSettings.showMap,
                          });
                        }}
                      />
                    </FloatingTooltip>
                  </div>
                </div>
                <div
                  ref={ownershipPanelRef}
                  className={styles.ownershipPanelBody}
                >
                  {layoutSettings.showCurrentOwnership && (
                    <div
                      ref={tableLineageRef}
                      className={styles.ownershipTableWrapper}
                      style={
                        ownershipMapSplit !== null && layoutSettings.showMap
                          ? { flex: `0 0 ${ownershipMapSplit * 100}%` }
                          : { flex: 1 }
                      }
                    >
                      <div
                        style={{
                          flex:
                            showLineage && lineageSplit !== null
                              ? `0 0 ${lineageSplit * 100}%`
                              : showLineage
                                ? '1 1 60%'
                                : '1 1 100%',
                          minHeight: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <OwnershipTanstackTable
                          ownershipGraph={subjectAssetGraph}
                          // Pass the raw unfiltered graph so encumbrance owner nodes
                          // outside the current location filter are still resolvable.
                          fullGraph={ownershipGraph}
                          documents={documents}
                          selectedNodeId={selectedNodeId}
                          onNodeRowClick={handleNodeRowClick}
                          onAssetClick={undefined}
                          onAssetLeave={undefined}
                          tableConfig={ownershipTableConfig}
                          groupBy={ownershipGroupBy}
                          onGroupByChange={setOwnershipGroupBy}
                          diffMode={docReviewMode ? ownershipDiffMode : 'all'}
                          onDiffModeChange={
                            docReviewMode ? setOwnershipDiffMode : undefined
                          }
                          beforeGraph={beforeDocGraph}
                        />
                      </div>
                      {showLineage && (
                        <>
                          <div
                            className={styles.dragHandleHorizontal}
                            onMouseDown={() =>
                              startDrag(
                                'vertical',
                                tableLineageRef,
                                setLineageSplit
                              )
                            }
                          />
                          <div
                            style={{
                              flex:
                                lineageSplit !== null
                                  ? `0 0 ${(1 - lineageSplit) * 100}%`
                                  : '1 1 40%',
                              minHeight: 0,
                              overflow: 'hidden',
                            }}
                          >
                            {citationNodeId ? (
                              <CitationTracingPanel
                                nodeId={citationNodeId}
                                graph={locationFilteredGraph}
                                documents={documents}
                                onClose={() => setShowLineage(false)}
                              />
                            ) : (
                              <div
                                style={{
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#6f6f6f',
                                  fontSize: '0.8125rem',
                                  borderTop: '1px solid #e0e0e0',
                                }}
                              >
                                Select an ownership node to view its lineage
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {layoutSettings.showCurrentOwnership &&
                    layoutSettings.showMap && (
                      <div
                        className={styles.dragHandleVertical}
                        onMouseDown={() =>
                          startDrag(
                            'horizontal',
                            ownershipPanelRef,
                            setOwnershipMapSplit
                          )
                        }
                      />
                    )}
                  {layoutSettings.showMap && (
                    <div
                      className={styles.ownershipMapWrapper}
                      style={
                        ownershipMapSplit !== null &&
                        layoutSettings.showCurrentOwnership
                          ? { flex: `0 0 ${(1 - ownershipMapSplit) * 100}%` }
                          : { flex: 1 }
                      }
                    >
                      <OwnershipMap
                        ownershipGraph={locationFilteredGraph}
                        geographies={mapGeographies}
                        selectedNodeId={selectedNodeId}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {(layoutSettings.showCurrentOwnership || layoutSettings.showMap) &&
              layoutSettings.showRunsheet && (
                <div
                  className={styles.dragHandleHorizontal}
                  onMouseDown={() =>
                    startDrag('vertical', tablePanelRef, setVerticalSplit)
                  }
                />
              )}
            {layoutSettings.showRunsheet && (
              <div
                className={styles.tablePanelLower}
                style={
                  verticalSplit !== null &&
                  (layoutSettings.showCurrentOwnership ||
                    layoutSettings.showMap)
                    ? { flex: `0 0 ${(1 - verticalSplit) * 100}%` }
                    : undefined
                }
              >
                {docReviewMode && reviewDocIndex !== null ? (
                  <DocumentReviewPanel
                    documentId={sortedDocuments[reviewDocIndex].documentId}
                    onBack={handleDocReviewBack}
                    ownershipGraph={locationFilteredGraph}
                    documents={documents}
                    startDrag={startDrag}
                  />
                ) : (
                  <ConveyanceRunsheet
                    ownershipGraph={runsheetGraph}
                    documents={documents}
                    documentDetails={{} as DocumentDetailMap}
                    totalDocumentCount={totalDocumentCount}
                    selectedEdgeIds={selectedEdgeIds}
                    selectedDocumentId={selectedDocumentId}
                    selectedTransactionId={selectedTransactionId}
                    selectedNodeId={selectedNodeId}
                    onEdgeClick={handleRunsheetEdgeClick}
                    onNodeClick={handleNodeRowClick}
                    onDocumentClick={handleDocumentClick}
                    onTransactionClick={handleTransactionClick}
                    onEnterDocReview={handleEnterDocReview}
                    startDate={selectedStartDate}
                    endDate={selectedDate}
                    geographies={allGeographies}
                    onAssetClick={undefined}
                    onAssetLeave={undefined}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chain Assistant - reserved for future integration */}
      {/* Asset Preview — disabled for now */}
    </div>
  );
}
